'use client'

import { ReactNode, useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useAuth } from '@/lib/auth/context'
import { isAdminUserClient } from '@/lib/admin/allowlist'
import { ADMIN_HOME, isUserAppRoute } from '@/lib/admin/routes'
import { AppSidebar } from './app-sidebar'

const COLLAPSE_KEY = 'calculamei:sidebar:collapsed'

/**
 * Shell da área logada — sidebar fixa em desktop, drawer em mobile.
 * A navbar global (Header) continua no layout raiz; a sidebar complementa
 * a navegação dentro das rotas do app.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, isSignedIn, isLoading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [collapsed, setCollapsed] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if (!isLoading && isAdminUserClient(user) && isUserAppRoute(pathname)) {
      router.replace(ADMIN_HOME)
    }
  }, [isLoading, user, pathname, router])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const saved = localStorage.getItem(COLLAPSE_KEY)
    if (saved === '1') setCollapsed(true)
    setHydrated(true)
  }, [])

  function toggle() {
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      return next
    })
  }

  if (isLoading || !isSignedIn) {
    return <>{children}</>
  }

  if (isAdminUserClient(user)) {
    return <>{children}</>
  }

  const cols = !hydrated || !collapsed ? 'lg:grid-cols-[248px_1fr]' : 'lg:grid-cols-[64px_1fr]'

  // Páginas públicas FUNCIONAIS (calculadoras + ferramentas) vistas logado
  // adotam a proporção da área logada (frame do app, conteúdo alinhado à
  // esquerda) via .app-embed. Conteúdo de leitura (blog/glossário) fica intacto.
  const EMBED_PREFIXES = [
    '/calculadoras',
    '/comprovante-de-faturamento-mei',
    '/quanto-posso-me-pagar-mei',
    '/dre-simplificado-mei',
  ]
  const embed = EMBED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))

  return (
    <div className={`lg:grid ${cols} transition-[grid-template-columns] duration-200`}>
      <AppSidebar collapsed={hydrated && collapsed} onToggleCollapsed={toggle} />
      <div className={`min-w-0${embed ? ' app-embed' : ''}`}>{children}</div>
    </div>
  )
}
